import React, { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "./cn";

/**
 * One row in the list.
 *
 * Three lines rather than one, because the things this picker chooses between
 * are all known by more than one name — a tax section by its number, its
 * description and the number it replaced; a currency by its code, its name and
 * its symbol. Which one somebody is looking for is not knowable in advance, so
 * all of them are shown and all of them are searched.
 */
export interface ComboOption {
  /** What `onChange` hands back. Unique within the list. */
  value: string;
  /** The bold first line in the list. */
  label: string;
  /** Under it. */
  detail?: string;
  /** Fainter still, already worded by the caller. */
  note?: string;
  /** The closed control's text. Falls back to `label`. */
  button?: string;
  /** A small line under the closed control, once something is chosen. */
  closed?: string;
  /** Everything worth matching a search against, including `value` if it helps. */
  search: string[];
}

/**
 * Choosing one of a long, closed list.
 *
 * A native `<select>` cannot do this job. Its type-ahead matches the start of
 * the option text and nothing else, so a CA who thinks "194H" — which is how
 * every one of them thinks, the Act 2025 names being two months old — has no
 * way to get there except scrolling. A filter box beside the select fixes the
 * searching and leaves two controls for one decision.
 *
 * So this is a combobox, the same shape as Zoho's own pickers: a button showing
 * the current choice, and a panel with the search at the top and the list under
 * it.
 *
 * Keyboard throughout: arrows move, Enter takes, Escape closes and puts focus
 * back on the button. Typing goes to the search box, which takes focus when the
 * panel opens.
 *
 * Written generically because the second caller wanted the identical widget:
 * `SectionPicker` chooses among sixty-odd Income Tax sections, and the currency
 * step among Zoho's 176 currencies. A copy would have drifted the first time
 * one of them was fixed.
 */
export function ComboPicker({
  options,
  value,
  onChange,
  disabled,
  placeholder = "Select",
  searchPlaceholder = "Search",
  noun,
}: {
  options: ComboOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Plural, for the footer count: "sections", "currencies". */
  noun: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [cursor, setCursor] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const chosen = options.find((option) => option.value === value);

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === "") return options;
    return options.filter((option) =>
      option.search.some((field) => (field ?? "").toLowerCase().includes(needle))
    );
  }, [options, search]);

  // Closing on an outside click rather than on blur: blur fires when focus moves
  // from the search box into the list, which would shut the panel mid-choice.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setSearch("");
  }, [open]);

  // The cursor is an index into a list that shrinks as somebody types, so it has
  // to come back to the top rather than point past the end.
  useEffect(() => {
    setCursor(0);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.children[cursor]?.scrollIntoView({ block: "nearest" });
  }, [cursor, open]);

  function take(option: ComboOption) {
    onChange(option.value);
    setOpen(false);
    buttonRef.current?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (matches.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setCursor((current) => (current + step + matches.length) % matches.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const option = matches[cursor];
      if (option) take(option);
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (!open && (event.key === "ArrowDown" || event.key === "Enter")) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          "flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-background px-2 text-left text-sm",
          "disabled:cursor-not-allowed disabled:opacity-60",
          open && "ring-2 ring-ring/40"
        )}
      >
        <span className={cn("min-w-0 truncate", chosen === undefined && "text-muted-foreground")}>
          {chosen ? chosen.button || chosen.label : placeholder}
        </span>
        <span aria-hidden className="shrink-0 text-xs text-muted-foreground">
          ▾
        </span>
      </button>

      {/* The chosen row's other names, so the closed control still says which
          one was picked without opening it again. */}
      {chosen?.closed && !open && (
        <span className="mt-1 block truncate text-[11px] text-muted-foreground">{chosen.closed}</span>
      )}

      {open && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-background shadow-lg">
          <div className="border-b p-2">
            <input
              ref={searchRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              className="h-8 w-full rounded border bg-background px-2 text-sm"
            />
          </div>

          {matches.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">Nothing matches "{search.trim()}".</p>
          ) : (
            <ul ref={listRef} role="listbox" className="max-h-72 overflow-y-auto py-1">
              {matches.map((option, index) => {
                const active = index === cursor;
                const current = option.value === value;

                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={current}
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => take(option)}
                    className={cn(
                      "cursor-pointer px-3 py-1.5",
                      active && "bg-primary/10",
                      current && "bg-primary/15"
                    )}
                  >
                    <span className="block text-sm font-medium">{option.label}</span>
                    {option.detail && (
                      <span className="block text-xs text-muted-foreground">{option.detail}</span>
                    )}
                    {option.note && (
                      <span className="block font-mono text-[11px] text-muted-foreground/80">
                        {option.note}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <div className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">
            {matches.length === options.length
              ? `${options.length} ${noun}`
              : `${matches.length} of ${options.length}`}
          </div>
        </div>
      )}
    </div>
  );
}
